/**
 * ContextMenu — Right-click menu
 * Per PLAN.md ui/ContextMenu.js and PRD §5.5
 */

import { eventBus } from '../core/EventBus.js';

export class ContextMenu {
    /**
     * @param {HTMLElement} container 
     */
    constructor(container) {
        this.container = container;
        this.element = null;
        this.isVisible = false;
        this.targetNodeId = null;

        this.init();
    }

    init() {
        this.element = document.createElement('div');
        this.element.className = 'context-menu';
        this.element.style.display = 'none';

        this.container.appendChild(this.element);

        // Close on click outside
        document.addEventListener('click', (e) => {
            if (this.isVisible && !this.element.contains(e.target)) {
                this.hide();
            }
        });

        // Close on escape
        document.addEventListener('keydown', (e) => {
            if (this.isVisible && e.key === 'Escape') {
                this.hide();
            }
        });
    }

    /**
     * Show context menu for a node
     * @param {string} nodeId 
     * @param {number} x - Screen X
     * @param {number} y - Screen Y
     */
    showForNode(nodeId, x, y) {
        this.targetNodeId = nodeId;

        this.element.innerHTML = `
      <div class="context-menu-item" data-action="edit">
        Edit
        <span class="shortcut">Return</span>
      </div>
      <div class="context-menu-item" data-action="duplicate">
        Duplicate
        <span class="shortcut">⌘D</span>
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-item" data-action="group">
        Group
        <span class="shortcut">⌘G</span>
      </div>
      <div class="context-menu-item" data-action="copyLatex">
        Copy LaTeX
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-item" data-action="delete" style="color: #cc0000;">
        Delete
        <span class="shortcut">⌫</span>
      </div>
    `;

        this.bindItemEvents();
        this.show(x, y);
    }

    /**
     * Show context menu for canvas (empty space)
     * @param {number} x - Screen X
     * @param {number} y - Screen Y
     * @param {{worldX: number, worldY: number}} worldPos 
     */
    showForCanvas(x, y, worldPos) {
        this.targetNodeId = null;
        this.worldPos = worldPos;

        this.element.innerHTML = `
      <div class="context-menu-item" data-action="addEquation">
        Add Equation
        <span class="shortcut">N</span>
      </div>
      <div class="context-menu-item" data-action="addText">
        Add Text Node
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-item" data-action="paste">
        Paste
        <span class="shortcut">⌘V</span>
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-item" data-action="zoomFit">
        Zoom to Fit
        <span class="shortcut">⌘0</span>
      </div>
    `;

        this.bindItemEvents();
        this.show(x, y);
    }

    /**
     * Show context menu for connection
     * @param {string} connectionId 
     * @param {number} x 
     * @param {number} y 
     */
    showForConnection(connectionId, x, y) {
        this.targetConnectionId = connectionId;

        this.element.innerHTML = `
      <div class="context-menu-item" data-action="deleteConnection">
        Delete Connection
        <span class="shortcut">⌫</span>
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-item" data-action="straighten">
        Straighten
      </div>
    `;

        this.bindItemEvents();
        this.show(x, y);
    }

    /**
     * Bind click events to menu items
     */
    bindItemEvents() {
        this.element.querySelectorAll('.context-menu-item').forEach(item => {
            item.addEventListener('click', () => {
                this.handleAction(item.dataset.action);
                this.hide();
            });
        });
    }

    /**
     * Handle menu action
     * @param {string} action 
     */
    handleAction(action) {
        eventBus.emit('contextmenu:action', {
            action,
            nodeId: this.targetNodeId,
            connectionId: this.targetConnectionId,
            worldPos: this.worldPos
        });
    }

    /**
     * Show menu at position
     * @param {number} x 
     * @param {number} y 
     */
    show(x, y) {
        this.element.style.display = 'block';
        this.isVisible = true;

        // Position menu
        const rect = this.element.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Adjust if menu would go off screen
        let finalX = x;
        let finalY = y;

        if (x + rect.width > viewportWidth - 10) {
            finalX = viewportWidth - rect.width - 10;
        }
        if (y + rect.height > viewportHeight - 10) {
            finalY = viewportHeight - rect.height - 10;
        }

        this.element.style.left = `${finalX}px`;
        this.element.style.top = `${finalY}px`;
    }

    /**
     * Hide menu
     */
    hide() {
        this.element.style.display = 'none';
        this.isVisible = false;
        this.targetNodeId = null;
        this.targetConnectionId = null;
    }

    /**
     * Destroy menu
     */
    destroy() {
        this.element.remove();
    }
}
